/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { AlertTriangle, Trash2, Loader2, Save } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ManageJurisdictionsModal, { ALL_STATES } from "@/components/ManageJurisdictionsModal";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const REVENUE_TIERS = ["Under $100k", "$100k-$500k", "$500k-$2M", "$2M+"];
const TRANSACTION_TIERS = ["Under 50", "50-100", "100-200", "200+"];

const slugifyTier = (t) =>
  t
    .toLowerCase()
    .replace(/\$/g, "")
    .replace(/\+/g, "plus")
    .replace(/[^\w]+/g, "-")
    .replace(/^-|-$/g, "");

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Profile state
  const [loading, setLoading] = useState(true);
  const [storeUrl, setStoreUrl] = useState("");
  const [revenueTier, setRevenueTier] = useState("");
  const [transactionTier, setTransactionTier] = useState("");
  const [scoreMode, setScoreMode] = useState("estimated");
  const [originalProfile, setOriginalProfile] = useState({
    shopify_store_url: "",
    annual_revenue_tier: "",
    annual_transaction_tier: "",
    score_mode: "estimated",
  });
  const [saving, setSaving] = useState(false);

  // Jurisdictions state
  const [jurisSelected, setJurisSelected] = useState([]);
  const [jurisOriginal, setJurisOriginal] = useState([]);
  const [jurisModalOpen, setJurisModalOpen] = useState(false);
  const [jurisSaving, setJurisSaving] = useState(false);

  // Delete-account state
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = confirmText.trim() === "DELETE";

  useEffect(() => {
    api.get("/audit/me/profile").then(({ data }) => {
      const p = data?.profile || {};
      setStoreUrl(p.shopify_store_url || "");
      setRevenueTier(p.annual_revenue_tier || "");
      setTransactionTier(p.annual_transaction_tier || "");
      setScoreMode(p.score_mode || "estimated");
      setOriginalProfile({
        shopify_store_url: p.shopify_store_url || "",
        annual_revenue_tier: p.annual_revenue_tier || "",
        annual_transaction_tier: p.annual_transaction_tier || "",
        score_mode: p.score_mode || "estimated",
      });
      setJurisSelected(p.selected_jurisdictions || []);
      setJurisOriginal(p.selected_jurisdictions || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const profileDirty =
    storeUrl !== originalProfile.shopify_store_url ||
    revenueTier !== originalProfile.annual_revenue_tier ||
    transactionTier !== originalProfile.annual_transaction_tier ||
    scoreMode !== originalProfile.score_mode;

  const handleSave = async () => {
    if (!storeUrl.trim()) return toast.error("Store URL is required");
    if (!revenueTier) return toast.error("Select a revenue tier");
    if (scoreMode === "estimated" && !transactionTier) {
      return toast.error("Select a transaction range for Quick Estimate mode");
    }
    setSaving(true);
    try {
      const { data } = await api.post("/audit/me/general", {
        shopify_store_url: storeUrl.trim(),
        annual_revenue_tier: revenueTier,
        annual_transaction_tier: scoreMode === "estimated" ? transactionTier : null,
        score_mode: scoreMode,
      });
      const next = {
        shopify_store_url: data.shopify_store_url || storeUrl.trim(),
        annual_revenue_tier: data.annual_revenue_tier || revenueTier,
        annual_transaction_tier: scoreMode === "estimated" ? transactionTier : "",
        score_mode: scoreMode,
      };
      setOriginalProfile(next);
      setStoreUrl(next.shopify_store_url);
      setRevenueTier(next.annual_revenue_tier);
      if (scoreMode === "verified") {
        toast.success(
          "Settings saved. To finish switching to Verified, re-run onboarding and enter exact per-state numbers.",
        );
      } else {
        toast.success("Settings saved — your cached audit was invalidated.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleJuris = (abbr) => {
    setJurisSelected((s) => s.includes(abbr) ? s.filter((x) => x !== abbr) : [...s, abbr]);
  };

  const saveJurisdictions = async () => {
    if (jurisSelected.length === 0) return toast.error("At least one jurisdiction must be selected.");
    setJurisSaving(true);
    try {
      await api.post("/audit/me/jurisdictions", { selected_jurisdictions: jurisSelected });
      setJurisOriginal(jurisSelected);
      toast.success("Jurisdictions updated — cached audit invalidated. Run a fresh audit from your dashboard.");
      setJurisModalOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save jurisdictions");
    } finally {
      setJurisSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/delete-account");
      toast.success("Your account has been scheduled for deletion.");
      setTimeout(() => {
        logout();
        navigate("/");
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete account.");
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" offset={80} />
      <section className="max-w-3xl mx-auto px-6 py-12" data-testid="settings-page">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Account</div>
        <h1 className="font-heading font-extrabold text-3xl text-[var(--rg-text-strong)] mb-6">Settings</h1>

        {/* Account info card */}
        <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 mb-8" data-testid="settings-account-card">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-muted)] mb-1">Email</div>
          <div className="text-[var(--rg-text-strong)] font-mono text-sm break-all" data-testid="settings-email">
            {user?.email}
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-muted)] mb-1">Plan</div>
          <div className="text-[var(--rg-text-strong)] text-sm capitalize" data-testid="settings-tier">
            {user?.tier || "free"}
          </div>
        </div>

        {/* GENERAL SETTINGS — store URL + revenue tier */}
        <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 mb-8" data-testid="settings-general">
          <h2 className="font-heading font-bold text-xl text-[var(--rg-text-strong)] mb-1">General Settings</h2>
          <p className="text-xs text-[var(--rg-text)] mb-5">
            Update your store URL and revenue tier. Changes invalidate your cached audit and trigger a fresh calculation on your next run.
          </p>

          <div className="space-y-4">
            <div>
              <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Shopify Store URL</Label>
              <Input
                placeholder="your-store.com  or  your-store.myshopify.com"
                value={storeUrl}
                onChange={(e) => setStoreUrl(e.target.value)}
                className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 w-full max-w-xl"
                data-testid="settings-store-url"
                disabled={loading || saving}
              />
            </div>

            <div>
              <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Annual Revenue Tier</Label>
              <Select
                key={`rev-tier-${revenueTier || "empty"}`}
                value={revenueTier}
                onValueChange={setRevenueTier}
                disabled={loading || saving}
              >
                <SelectTrigger className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 w-full max-w-xl" data-testid="settings-revenue-tier">
                  <SelectValue placeholder="Select revenue tier" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]">
                  {REVENUE_TIERS.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`settings-tier-opt-${slugifyTier(t)}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Score Mode switch — Quick Estimate ↔ Verified */}
            <div data-testid="settings-score-mode">
              <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Score Mode</Label>
              <div className="grid sm:grid-cols-2 gap-3 mt-2 max-w-xl">
                <button
                  type="button"
                  onClick={() => setScoreMode("estimated")}
                  disabled={loading || saving}
                  className={`text-left p-3 rounded-md border-2 transition-fast disabled:opacity-50 ${
                    scoreMode === "estimated"
                      ? "border-[var(--rg-sage)] bg-[var(--rg-sage-soft)]"
                      : "border-[var(--rg-border)] bg-[var(--rg-surface)] hover:border-[var(--rg-border-hi)]"
                  }`}
                  data-testid="settings-mode-estimated"
                >
                  <div className="text-[var(--rg-text-strong)] font-bold text-sm mb-1">Quick Estimate</div>
                  <p className="text-[10px] text-[var(--rg-text)] leading-snug">
                    Tier ranges. Score labelled <strong className="text-[var(--rg-amber)]">Estimated</strong>. Share badge disabled.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setScoreMode("verified")}
                  disabled={loading || saving}
                  className={`text-left p-3 rounded-md border-2 transition-fast disabled:opacity-50 ${
                    scoreMode === "verified"
                      ? "border-[var(--rg-sage)] bg-[var(--rg-sage-soft)]"
                      : "border-[var(--rg-border)] bg-[var(--rg-surface)] hover:border-[var(--rg-border-hi)]"
                  }`}
                  data-testid="settings-mode-verified"
                >
                  <div className="text-[var(--rg-text-strong)] font-bold text-sm mb-1">Exact Numbers</div>
                  <p className="text-[10px] text-[var(--rg-text)] leading-snug">
                    Per-state numbers. Score labelled <strong className="text-[var(--rg-sage)]">Verified</strong>. Unlocks share badge.
                  </p>
                </button>
              </div>
              {scoreMode === "verified" && originalProfile.score_mode !== "verified" && (
                <p className="text-[11px] text-[var(--rg-amber)] mt-2" data-testid="settings-verified-handoff">
                  ⚠️ After saving, re-run onboarding to enter exact per-state revenue + transactions and unlock your Verified score.
                </p>
              )}
            </div>

            {/* Transaction tier — only relevant in Quick Estimate mode */}
            {scoreMode === "estimated" && (
              <div data-testid="settings-transaction-tier-wrap">
                <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Annual Transaction Range</Label>
                <Select
                  key={`txn-tier-${transactionTier || "empty"}`}
                  value={transactionTier}
                  onValueChange={setTransactionTier}
                  disabled={loading || saving}
                >
                  <SelectTrigger className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 w-full max-w-xl" data-testid="settings-transaction-tier">
                    <SelectValue placeholder="Select transaction range" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]">
                    {TRANSACTION_TIERS.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`settings-txn-opt-${slugifyTier(t)}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="mt-5 flex flex-col gap-2">
            <Button
              onClick={handleSave}
              disabled={saving || loading || !profileDirty}
              className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold disabled:opacity-40 w-fit"
              data-testid="settings-save-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Changes</>
              )}
            </Button>
            <p className="text-xs text-[var(--rg-muted)] italic mt-1" data-testid="settings-save-warning">
              ⚠️ Modifying your store configuration will invalidate your current cached audit and trigger a fresh calculation on your next audit run.
            </p>
          </div>
        </div>

        {/* JURISDICTIONS */}
        <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 mb-8" data-testid="settings-jurisdictions">
          <h2 className="font-heading font-bold text-xl text-[var(--rg-text-strong)] mb-1">Selected Jurisdictions</h2>
          <p className="text-xs text-[var(--rg-text)] mb-4">
            You currently track <strong className="text-[var(--rg-text-strong)]">{jurisSelected.length}</strong> of {ALL_STATES.length} U.S. jurisdictions.
          </p>
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="settings-juris-list">
            {jurisSelected.length === 0 ? (
              <span className="text-xs text-[var(--rg-muted)] italic">No jurisdictions selected yet.</span>
            ) : jurisSelected.map((abbr) => (
              <span
                key={abbr}
                className="inline-block px-2 py-0.5 rounded-sm text-[11px] font-mono font-bold text-[var(--rg-on-sage)] bg-[var(--rg-sage)]"
                data-testid={`settings-juris-chip-${abbr}`}
              >
                {abbr}
              </span>
            ))}
          </div>
          <Button
            onClick={() => setJurisModalOpen(true)}
            variant="outline"
            disabled={loading}
            className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"
            data-testid="settings-manage-juris-btn"
          >
            Manage Jurisdictions
          </Button>
        </div>

        {/* DANGER ZONE */}
        <div
          className="rounded-md border-2 border-[var(--rg-red)] bg-[var(--rg-red)]/5 p-6"
          data-testid="danger-zone"
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-[var(--rg-red)]" />
            <h2 className="font-heading font-bold text-xl text-[var(--rg-text-strong)]">Delete Account</h2>
          </div>
          <p className="text-sm text-[var(--rg-text)] mb-5 leading-relaxed">
            Permanently delete your account and all associated data. This action cannot be undone. All store profiles, audit history, scores, and personal information will be permanently removed within 30 days.
          </p>
          <Button
            onClick={() => setOpen(true)}
            className="bg-[var(--rg-red)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-red)] font-semibold"
            data-testid="delete-account-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete My Account and All Data
          </Button>
        </div>
      </section>

      {/* Manage jurisdictions modal */}
      <ManageJurisdictionsModal
        open={jurisModalOpen}
        onOpenChange={setJurisModalOpen}
        selected={jurisSelected}
        onToggle={toggleJuris}
        onSelectAll={() => setJurisSelected([...ALL_STATES])}
        onDeselectAll={() => setJurisSelected([])}
        onSave={saveJurisdictions}
        saving={jurisSaving}
      />

      {/* Delete-account confirm dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-red)] text-[var(--rg-text-strong)]" data-testid="delete-confirm-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Confirm account deletion</DialogTitle>
            <DialogDescription className="text-[var(--rg-text)]">
              Type <span className="font-mono font-bold text-[var(--rg-text-strong)]">DELETE</span> to confirm account deletion:
            </DialogDescription>
          </DialogHeader>
          <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Confirmation</Label>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE"
            className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]"
            data-testid="delete-confirm-input"
            autoFocus
          />
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
                setConfirmText("");
              }}
              className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"
              data-testid="delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={!canSubmit || submitting}
              className="bg-[var(--rg-red)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-red)] font-semibold"
              data-testid="delete-confirm-btn"
            >
              {submitting ? "Deleting…" : "Permanently Delete My Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
